import { getApiOrigin } from '@/config/env';
import { getToken } from '@/utils/auth';

export type DuitNowQrMode = 'default' | 'individual';

export type SiteSettings = {
  duitNowQrMode: DuitNowQrMode;
  hasDefaultDuitNowQr: boolean;
  paymentProofUrl: string;
  stockTrackerUrl: string;
};

function authHeaders(): HeadersInit {
  return {
    Authorization: `Bearer ${getToken()}`,
    'Content-Type': 'application/json',
  };
}

async function parseJsonError(res: Response, fallback: string): Promise<never> {
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  throw new Error(data.error || fallback);
}

function parseSettings(data: { settings?: SiteSettings }): SiteSettings | null {
  const settings = data.settings;
  if (
    !settings ||
    (settings.duitNowQrMode !== 'default' && settings.duitNowQrMode !== 'individual')
  ) {
    return null;
  }
  return {
    duitNowQrMode: settings.duitNowQrMode,
    hasDefaultDuitNowQr: Boolean(settings.hasDefaultDuitNowQr),
    paymentProofUrl: typeof settings.paymentProofUrl === 'string' ? settings.paymentProofUrl : '',
    stockTrackerUrl: typeof settings.stockTrackerUrl === 'string' ? settings.stockTrackerUrl : '',
  };
}

export async function fetchSiteSettings(): Promise<SiteSettings> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/site-settings`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    settings?: SiteSettings;
    error?: string;
  };

  const settings = parseSettings(data);
  if (!res.ok || !data.ok || !settings) {
    throw new Error(data.error || 'Failed to load site settings');
  }

  return settings;
}

export async function updateSiteSettings(input: {
  duitNowQrMode?: DuitNowQrMode;
  paymentProofUrl?: string;
  stockTrackerUrl?: string;
}): Promise<SiteSettings> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/site-settings`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    settings?: SiteSettings;
    error?: string;
  };

  const settings = parseSettings(data);
  if (!res.ok || !data.ok || !settings) {
    await parseJsonError(res, data.error || 'Failed to update site settings');
  }

  return settings!;
}

export async function fetchDefaultDuitNowQrBlob(): Promise<Blob> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/site-settings/default-duitnow-qr`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) {
    await parseJsonError(res, 'Failed to load DuitNow QR');
  }
  return res.blob();
}

export async function uploadDefaultDuitNowQr(file: File): Promise<SiteSettings> {
  const image = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error('Failed to read image'));
    reader.readAsDataURL(file);
  });

  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/site-settings/default-duitnow-qr`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ image }),
  });
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    settings?: SiteSettings;
    error?: string;
  };

  const settings = parseSettings(data);
  if (!res.ok || !data.ok || !settings) {
    throw new Error(data.error || 'Failed to upload DuitNow QR');
  }

  return settings;
}

export async function deleteDefaultDuitNowQr(): Promise<SiteSettings> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/site-settings/default-duitnow-qr`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    settings?: SiteSettings;
    error?: string;
  };

  const settings = parseSettings(data);
  if (!res.ok || !data.ok || !settings) {
    throw new Error(data.error || 'Failed to remove DuitNow QR');
  }

  return settings;
}

export type TestMobileNumber = {
  id: string;
  contact: string;
  display: string;
  completedCount: number;
  createdAt: string;
};

function parseTestMobile(row: unknown): TestMobileNumber | null {
  if (!row || typeof row !== 'object') return null;
  const r = row as Record<string, unknown>;
  if (typeof r.id !== 'string' || typeof r.contact !== 'string' || typeof r.display !== 'string') {
    return null;
  }
  return {
    id: r.id,
    contact: r.contact,
    display: r.display,
    completedCount: typeof r.completedCount === 'number' ? r.completedCount : 0,
    createdAt: typeof r.createdAt === 'string' ? r.createdAt : '',
  };
}

export async function fetchTestMobileNumbers(): Promise<TestMobileNumber[]> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/site-settings/test-mobile-numbers`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    numbers?: unknown;
    error?: string;
  };
  if (!res.ok || !data.ok || !Array.isArray(data.numbers)) {
    throw new Error(data.error || 'Failed to load test mobile numbers');
  }
  return data.numbers.map(parseTestMobile).filter((row): row is TestMobileNumber => row != null);
}

export async function addTestMobileNumber(mobile: string): Promise<TestMobileNumber> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/site-settings/test-mobile-numbers`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ mobile }),
  });
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    number?: unknown;
    error?: string;
  };
  const number = parseTestMobile(data.number);
  if (!res.ok || !data.ok || !number) {
    throw new Error(data.error || 'Failed to add mobile number');
  }
  return number;
}

export async function deleteTestMobileNumber(id: string): Promise<void> {
  const apiUrl = getApiOrigin();
  const res = await fetch(`${apiUrl}/api/v1/admin/site-settings/test-mobile-numbers/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
  if (!res.ok || !data.ok) {
    throw new Error(data.error || 'Failed to delete mobile number');
  }
}
